<template>
  <div>
    <v-row class="my-1">
      <v-col cols="6">
        <v-autocomplete
          label="Provincia"
          :items="provincias"
          item-text="nombre"
          item-value="codigo"
          :value="selectedProvincia && selectedProvincia.codigo"
          :rules="requiredRules"
          :disabled="disabled"
          :loading="loadingProvincias"
          :error-messages="catalogError"
          outlined
          dense
          hide-details="auto"
          @change="onProvinciaChange"
        ></v-autocomplete>
      </v-col>
      <v-col cols="6">
        <v-autocomplete
          label="Localidad"
          :items="localidadItems"
          item-text="text"
          item-value="codigo"
          :value="selectedLocalidad && selectedLocalidad.codigo"
          :rules="requiredRules"
          :disabled="disabled || !selectedProvincia"
          :loading="loadingLocalidades"
          :error-messages="localidadesError"
          outlined
          dense
          hide-details="auto"
          @change="onLocalidadChange"
        ></v-autocomplete>
      </v-col>
    </v-row>

    <v-row class="my-1">
      <v-col cols="5">
        <v-text-field
          label="Calle"
          :value="value.street"
          :rules="requiredRules"
          :disabled="disabled"
          outlined
          dense
          hide-details="auto"
          @input="update({ street: $event })"
        ></v-text-field>
      </v-col>
      <v-col cols="3">
        <v-text-field
          label="Número (altura)"
          :value="value.number"
          :rules="requiredRules"
          :disabled="disabled"
          outlined
          dense
          hide-details="auto"
          @input="update({ number: $event })"
        ></v-text-field>
      </v-col>
      <v-col cols="4">
        <v-text-field
          label="Piso o departamento"
          :value="value.extra"
          :disabled="disabled"
          outlined
          dense
          hide-details="auto"
          @input="update({ extra: $event })"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row class="my-1">
      <v-col cols="6">
        <v-text-field
          label="Barrio (opcional)"
          :value="value.neighborhood"
          :disabled="disabled"
          outlined
          dense
          hide-details="auto"
          @input="update({ neighborhood: $event })"
        ></v-text-field>
      </v-col>
      <v-col cols="4">
        <v-text-field
          label="Código postal (opcional)"
          :value="value.postalCode"
          :rules="postalCodeRules"
          :disabled="disabled"
          maxlength="8"
          outlined
          dense
          hide-details="auto"
          @input="update({ postalCode: $event })"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row class="my-1">
      <v-col cols="12">
        <v-text-field
          label="Referencias (opcional)"
          :value="value.references"
          :disabled="disabled"
          outlined
          dense
          hide-details="auto"
          @input="update({ references: $event })"
        ></v-text-field>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ArgentinaGeoService from '../services/argentina-geo.service';
import { emptyAddress, normalizeAddress, isValidArgentinePostalCode } from '../utils/address.utils';

const CATALOG_ERROR = 'No se pudo cargar el catálogo de Argentina';

const sameName = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

// Dirección de Argentina con el catálogo de Georef: Provincia → Localidad (con su
// departamento a la vista, porque hay nombres repetidos) → calle, altura y el resto.
// v-model: la dirección completa de address.utils (region/commune como { code, name }).
// El departamento solo se muestra: la localidad se identifica por su código.
export default {
  name: 'ArgentinaAddressFields',
  props: {
    value: { type: Object, default: () => emptyAddress() },
    disabled: { type: Boolean, default: false },
    // Exige los obligatorios. El padre lo activa cuando hay (o hubo) dirección:
    // el backend rechaza una dirección de Argentina incompleta.
    required: { type: Boolean, default: false }
  },
  data() {
    return {
      provincias: [],
      localidades: [],
      loadingProvincias: false,
      loadingLocalidades: false,
      catalogError: '',
      localidadesError: '',
      // Provincia cuyas localidades se pidieron por última vez: una respuesta de otra se descarta.
      requestedProvincia: null
    };
  },
  computed: {
    // Por código; si la dirección guardada no lo trae, por nombre.
    selectedProvincia() {
      const { code, name } = normalizeAddress(this.value).region;
      return (
        this.provincias.find(p => code && p.codigo === code) ||
        this.provincias.find(p => name && sameName(p.nombre, name)) ||
        null
      );
    },
    localidadItems() {
      return this.localidades.map(l => ({ ...l, text: `${l.nombre} — ${l.departamento}` }));
    },
    // Por código; por nombre solo si hay una única coincidencia: con un nombre repetido
    // (por ejemplo "San José") se deja vacía antes que elegir en silencio la equivocada.
    selectedLocalidad() {
      const { code, name } = normalizeAddress(this.value).commune;
      const byCode = code && this.localidadItems.find(l => l.codigo === code);
      if (byCode) return byCode;
      const byName = name ? this.localidadItems.filter(l => sameName(l.nombre, name)) : [];
      return byName.length === 1 ? byName[0] : null;
    },
    requiredRules() {
      return this.required ? [v => !!(v && String(v).trim()) || 'Este campo es requerido'] : [];
    },
    // El código postal es opcional: vacío es válido, aun con required.
    postalCodeRules() {
      return [v => !v || isValidArgentinePostalCode(v) || 'Formato: 1425 o C1425ABC'];
    }
  },
  watch: {
    selectedProvincia: {
      immediate: true,
      handler(provincia, previous) {
        if (provincia && provincia.codigo !== (previous && previous.codigo)) {
          this.loadLocalidades(provincia.codigo);
        } else if (!provincia) {
          this.requestedProvincia = null;
          this.localidades = [];
          this.localidadesError = '';
        }
      }
    }
  },
  mounted() {
    this.loadProvincias();
  },
  methods: {
    async loadProvincias() {
      this.loadingProvincias = true;
      const res = await ArgentinaGeoService.getProvincias();
      if (res.status === 'ok') {
        // Alfabético y no por código INDEC; copia para no reordenar la caché del servicio.
        this.provincias = [...res.data].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
        this.catalogError = '';
      } else {
        this.catalogError = CATALOG_ERROR;
      }
      this.loadingProvincias = false;
    },
    async loadLocalidades(provinciaCode) {
      this.requestedProvincia = provinciaCode;
      this.loadingLocalidades = true;
      const res = await ArgentinaGeoService.getLocalidades(provinciaCode);
      // Si mientras tanto se eligió otra provincia, esta respuesta ya no corresponde.
      if (this.requestedProvincia !== provinciaCode) return;
      if (res.status === 'ok') {
        this.localidades = res.data;
        this.localidadesError = '';
      } else {
        this.localidades = [];
        this.localidadesError = CATALOG_ERROR;
      }
      this.loadingLocalidades = false;
    },
    update(patch) {
      this.$emit('input', { ...normalizeAddress(this.value), ...patch });
    },
    onProvinciaChange(codigo) {
      const provincia = this.provincias.find(p => p.codigo === codigo);
      this.update({
        region: { code: provincia ? provincia.codigo : '', name: provincia ? provincia.nombre : '' },
        commune: { code: '', name: '' }
      });
    },
    onLocalidadChange(codigo) {
      const localidad = this.localidadItems.find(l => l.codigo === codigo);
      this.update({
        commune: { code: localidad ? localidad.codigo : '', name: localidad ? localidad.nombre : '' }
      });
    }
  }
};
</script>
